import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { RepeatIcon, SearchIcon } from '@chakra-ui/icons';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { getGoogleMapApiKey, GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID } from '../Utilities/Apikeys';
import { getTechniciansLocation } from '../Utilities/networks';
import { formatLastSeen } from '../Utilities/dateFormat';
import DataCard from './ui/DataCard';
import PageShell from './ui/PageShell';

const DEFAULT_CENTER = { lat: 12.9716, lng: 77.5946 };
const MAP_HEIGHT = 'calc(100vh - 300px)';
const MIN_MAP_HEIGHT = '520px';
const mapContainerStyle = {
  width: '100%',
  height: '100%',
  minHeight: MIN_MAP_HEIGHT,
  borderRadius: '1rem',
};

const STATUS_COLORS = {
  Pending: '#F59E0B',
  'In Progress': '#0891B2',
  Completed: '#22C55E',
};

function parseCoord(value) {
  const num = parseFloat(value);
  return Number.isNaN(num) ? null : num;
}

function complaintPosition(complaint) {
  const lat = parseCoord(complaint.latitude);
  const lng = parseCoord(complaint.longitude);
  if (lat == null || lng == null) return null;
  return { lat, lng };
}

function pinIcon(color) {
  return {
    path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
    fillColor: color,
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 2,
    scale: 1.35,
    anchor: { x: 12, y: 22 },
  };
}

const ComplaintsMap = ({ complaints = [] }) => {
  const apiKey = getGoogleMapApiKey();
  const mapRef = useRef(null);
  const { isLoaded: isMapScriptLoaded, loadError: mapLoadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [search, setSearch] = useState('');
  const [focusedComplaintId, setFocusedComplaintId] = useState(null);
  const [activeInfo, setActiveInfo] = useState(null);

  const complaintsWithLocation = useMemo(
    () => complaints
      .map((complaint) => {
        const position = complaintPosition(complaint);
        if (!position) return null;
        return { ...complaint, position };
      })
      .filter(Boolean),
    [complaints],
  );

  const techniciansById = useMemo(() => {
    const map = {};
    technicians.forEach((tech) => {
      map[tech.technicianId] = tech;
    });
    return map;
  }, [technicians]);

  const connectionLines = useMemo(() => {
    const lines = [];
    complaintsWithLocation.forEach((complaint) => {
      if (focusedComplaintId && complaint.id !== focusedComplaintId) {
        return;
      }
      const techIds = complaint.assignedTechnicians || [];
      techIds.forEach((techId) => {
        const tech = techniciansById[techId];
        if (!tech) return;
        lines.push({
          id: `${complaint.id}-${techId}`,
          complaintId: complaint.id,
          path: [
            { lat: tech.lat, lng: tech.lng },
            complaint.position,
          ],
        });
      });
    });
    return lines;
  }, [complaintsWithLocation, focusedComplaintId, techniciansById]);

  const sidebarComplaints = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return complaints;
    return complaints.filter((c) =>
      (c.customerName || c.customer_name || '').toLowerCase().includes(q)
      || (c.phone || '').toLowerCase().includes(q)
      || (c.address || '').toLowerCase().includes(q)
      || String(c.id).includes(q),
    );
  }, [complaints, search]);

  const fetchTechniciansLocation = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const response = await getTechniciansLocation();
      if (response.data?.status === 'success') {
        const data = response.data.data || [];
        const latestTechnicians = data
          .filter((item) => {
            const lat = parseCoord(item.latitude);
            const lng = parseCoord(item.longitude);
            return lat != null && lng != null && lat !== 0 && lng !== 0;
          })
          .map((item) => ({
            id: `tech_${item.technician_id}`,
            technicianId: item.technician_id,
            name: item.name || 'Unknown',
            phone: item.phone || 'N/A',
            area: item.area || 'N/A',
            lat: parseCoord(item.latitude),
            lng: parseCoord(item.longitude),
            lastSeen: item.datetime,
          }));
        setTechnicians(latestTechnicians);
        setLastUpdate(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }));
      } else {
        setError(response.data?.message || 'Failed to load technician locations');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load technician locations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTechniciansLocation();
  }, [fetchTechniciansLocation]);

  useEffect(() => {
    const interval = setInterval(() => fetchTechniciansLocation(true), 30000);
    return () => clearInterval(interval);
  }, [fetchTechniciansLocation]);

  const fitMapToPoints = useCallback((points) => {
    if (!mapRef.current || !window.google || points.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    mapRef.current.fitBounds(bounds, 72);
  }, []);

  useEffect(() => {
    const points = [];
    const complaintItems = focusedComplaintId
      ? complaintsWithLocation.filter((c) => c.id === focusedComplaintId)
      : complaintsWithLocation;

    complaintItems.forEach((c) => points.push(c.position));
    technicians.forEach((t) => points.push({ lat: t.lat, lng: t.lng }));

    if (points.length > 1) {
      fitMapToPoints(points);
    } else if (points.length === 1) {
      mapRef.current?.panTo(points[0]);
      mapRef.current?.setZoom(15);
    }
  }, [complaintsWithLocation, fitMapToPoints, focusedComplaintId, technicians]);

  const focusComplaint = useCallback((complaint) => {
    const position = complaintPosition(complaint);
    if (!position) return;
    setFocusedComplaintId(complaint.id);
    setActiveInfo({ type: 'complaint', data: complaint, position });
    mapRef.current?.panTo(position);
    mapRef.current?.setZoom(15);
  }, []);

  const showAllComplaints = useCallback(() => {
    setFocusedComplaintId(null);
    setActiveInfo(null);
    const points = [
      ...complaintsWithLocation.map((c) => c.position),
      ...technicians.map((t) => ({ lat: t.lat, lng: t.lng })),
    ];
    if (points.length > 0) {
      fitMapToPoints(points);
    }
  }, [complaintsWithLocation, fitMapToPoints, technicians]);

  const complaintMarkers = focusedComplaintId
    ? complaintsWithLocation.filter((c) => c.id === focusedComplaintId)
    : complaintsWithLocation;

  return (
    <Box>
      <PageShell
        title="Complaints Map"
        subtitle="Live technician positions, complaint locations, and assignment routes"
        action={
          <HStack spacing={3}>
            {lastUpdate && (
              <Badge colorScheme="brand" variant="subtle" px={3} py={1} borderRadius="full">
                Updated {lastUpdate}
              </Badge>
            )}
            <Button
              leftIcon={<RepeatIcon />}
              colorScheme="brand"
              size="sm"
              borderRadius="lg"
              isLoading={loading}
              onClick={() => fetchTechniciansLocation()}
            >
              Refresh
            </Button>
          </HStack>
        }
      />

      <HStack spacing={3} mb={4} flexWrap="wrap">
        <Badge px={3} py={1} borderRadius="full" bg="white" border="1px solid" borderColor="slate.200">
          {complaintsWithLocation.length} complaints on map
        </Badge>
        <Badge px={3} py={1} borderRadius="full" bg="blue.50" color="blue.700" border="1px solid" borderColor="blue.100">
          {technicians.length} technicians live
        </Badge>
        <Badge px={3} py={1} borderRadius="full" bg="indigo.50" color="indigo.700" border="1px solid" borderColor="indigo.100">
          {connectionLines.length} routes
        </Badge>
        {focusedComplaintId && (
          <Button size="xs" variant="outline" colorScheme="brand" onClick={showAllComplaints}>
            Show all complaints
          </Button>
        )}
      </HStack>

      {error && (
        <Alert status="warning" borderRadius="xl" mb={4}>
          <AlertIcon />
          <Text fontSize="sm">{error}</Text>
        </Alert>
      )}

      <Flex direction={{ base: 'column', lg: 'row' }} gap={4} align="stretch" minH={MIN_MAP_HEIGHT}>
        <DataCard flex={1} p={0} position="relative" overflow="hidden" minH={MIN_MAP_HEIGHT}>
          {loading && technicians.length === 0 && complaintsWithLocation.length === 0 && (
            <Flex
              position="absolute"
              inset={0}
              zIndex={2}
              bg="whiteAlpha.800"
              align="center"
              justify="center"
              direction="column"
              gap={3}
            >
              <Spinner size="lg" color="brand.500" thickness="3px" />
              <Text color="slate.600" fontSize="sm">Loading map data...</Text>
            </Flex>
          )}

          {!apiKey ? (
            <Flex align="center" justify="center" minH={MIN_MAP_HEIGHT} p={6}>
              <Text color="slate.500" textAlign="center">
                Add your Google Maps API key in Utilities/Apikeys.js to enable the map.
              </Text>
            </Flex>
          ) : mapLoadError ? (
            <Flex align="center" justify="center" minH={MIN_MAP_HEIGHT} p={6}>
              <Text color="red.500" textAlign="center">
                Failed to load Google Maps. Check the API key configuration.
              </Text>
            </Flex>
          ) : !isMapScriptLoaded ? (
            <Flex align="center" justify="center" minH={MIN_MAP_HEIGHT} p={6} gap={3}>
              <Spinner size="lg" color="brand.500" thickness="3px" />
              <Text color="slate.600" fontSize="sm">Loading map...</Text>
            </Flex>
          ) : (
            <Box h={MAP_HEIGHT} minH={MIN_MAP_HEIGHT}>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={DEFAULT_CENTER}
                  zoom={11}
                  onLoad={(map) => {
                    mapRef.current = map;
                  }}
                  options={{
                    fullscreenControl: true,
                    streetViewControl: false,
                    mapTypeControl: false,
                  }}
                >
                  {connectionLines.map((line) => (
                    <Polyline
                      key={line.id}
                      path={line.path}
                      options={{
                        strokeColor: focusedComplaintId ? '#4F46E5' : '#94A3B8',
                        strokeWeight: focusedComplaintId ? 4 : 2,
                        strokeOpacity: focusedComplaintId ? 0.9 : 0.55,
                      }}
                    />
                  ))}

                  {technicians.map((tech) => (
                    <Marker
                      key={tech.id}
                      position={{ lat: tech.lat, lng: tech.lng }}
                      icon={pinIcon('#2563EB')}
                      title={tech.name}
                      onClick={() => setActiveInfo({ type: 'technician', data: tech, position: { lat: tech.lat, lng: tech.lng } })}
                    />
                  ))}

                  {complaintMarkers.map((complaint) => (
                    <Marker
                      key={`complaint-${complaint.id}`}
                      position={complaint.position}
                      icon={pinIcon(STATUS_COLORS[complaint.status] || '#EF4444')}
                      title={complaint.customerName || complaint.customer_name}
                      onClick={() => focusComplaint(complaint)}
                    />
                  ))}

                  {activeInfo && (
                    <InfoWindow
                      position={activeInfo.position}
                      onCloseClick={() => setActiveInfo(null)}
                    >
                      <Box minW="180px" p={1}>
                        {activeInfo.type === 'complaint' ? (
                          <>
                            <Text fontWeight="700" color="slate.800">
                              #{activeInfo.data.id} {activeInfo.data.customerName || activeInfo.data.customer_name}
                            </Text>
                            <Text fontSize="sm" color="slate.600" mt={1}>{activeInfo.data.address}</Text>
                            <Badge mt={2} colorScheme={activeInfo.data.status === 'Completed' ? 'green' : 'blue'}>
                              {activeInfo.data.status}
                            </Badge>
                          </>
                        ) : (
                          <>
                            <Text fontWeight="700" color="slate.800">{activeInfo.data.name}</Text>
                            <Text fontSize="sm" color="slate.600" mt={1}>{activeInfo.data.phone}</Text>
                            <Text fontSize="xs" color="slate.500" mt={1}>{formatLastSeen(activeInfo.data.lastSeen)}</Text>
                          </>
                        )}
                      </Box>
                    </InfoWindow>
                  )}
                </GoogleMap>
            </Box>
          )}
        </DataCard>

        <DataCard
          w={{ base: 'full', lg: '360px' }}
          flexShrink={0}
          p={0}
          display="flex"
          flexDirection="column"
          maxH={MAP_HEIGHT}
          minH={{ base: '360px', lg: MIN_MAP_HEIGHT }}
        >
          <Box p={4} borderBottom="1px solid" borderColor="slate.100">
            <InputGroup size="md">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="slate.400" />
              </InputLeftElement>
              <Input
                placeholder="Search complaints..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                borderRadius="lg"
                bg="white"
              />
            </InputGroup>
            <Button mt={3} size="sm" width="full" variant="outline" colorScheme="brand" onClick={showAllComplaints}>
              Show all on map
            </Button>
          </Box>

          <Box flex={1} overflowY="auto" p={3}>
            <VStack align="stretch" spacing={2}>
              {sidebarComplaints.length === 0 && (
                <Text color="slate.500" fontSize="sm" textAlign="center" py={8}>
                  No complaints match your search.
                </Text>
              )}
              {sidebarComplaints.map((complaint) => {
                const hasLocation = complaintPosition(complaint) != null;
                const isFocused = focusedComplaintId === complaint.id;
                return (
                  <Box
                    key={complaint.id}
                    p={3}
                    borderRadius="xl"
                    border="1px solid"
                    borderColor={isFocused ? 'brand.300' : 'slate.100'}
                    bg={isFocused ? 'brand.50' : 'white'}
                    cursor={hasLocation ? 'pointer' : 'default'}
                    opacity={hasLocation ? 1 : 0.65}
                    transition="all 0.15s"
                    _hover={hasLocation ? { borderColor: 'brand.300', boxShadow: 'soft' } : undefined}
                    onClick={() => {
                      if (hasLocation) focusComplaint(complaint);
                    }}
                  >
                    <HStack justify="space-between" align="start">
                      <Box flex={1} minW={0}>
                        <Text fontWeight="600" color="slate.800" noOfLines={1}>
                          #{complaint.id} {complaint.customerName || complaint.customer_name}
                        </Text>
                        <Text fontSize="xs" color="slate.500" mt={1} noOfLines={2}>
                          {complaint.address || 'No address'}
                        </Text>
                        <HStack mt={2} spacing={2} flexWrap="wrap">
                          <Badge colorScheme={complaint.status === 'Completed' ? 'green' : complaint.status === 'In Progress' ? 'blue' : 'orange'}>
                            {complaint.status}
                          </Badge>
                          {!hasLocation && (
                            <Badge colorScheme="gray">No map pin</Badge>
                          )}
                        </HStack>
                        {complaint.technicianNames && (
                          <Text fontSize="xs" color="slate.500" mt={2} noOfLines={1}>
                            Tech: {complaint.technicianNames}
                          </Text>
                        )}
                      </Box>
                      {hasLocation && (
                        <Box color="brand.500" pt={1}>
                          <FaMapMarkerAlt />
                        </Box>
                      )}
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          </Box>
        </DataCard>
      </Flex>
    </Box>
  );
};

export default ComplaintsMap;
